import React, { useState } from 'react';
import { Card, Typography, Radio, Input, Button, Alert, message, Flex } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, QuestionCircleOutlined, CloseCircleOutlined, SendOutlined } from '@ant-design/icons';
import { PublicInvitationDetailDto, RsvpOption, JoinRequestDto } from '../../commons/data/types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export interface SessionRsvpBookingFormProps {
    invitation: PublicInvitationDetailDto;
    onSuccess: (joinRequest: JoinRequestDto) => void;
    onCancel: () => void;
}

export const SessionRsvpBookingForm: React.FC<SessionRsvpBookingFormProps> = ({
    invitation,
    onSuccess,
    onCancel
}) => {
    const { t } = useTranslation();
    const sessions = invitation.sessions || [];

    // Default first session to YES if available, others to YES
    const initialRsvps: Record<string, RsvpOption> = {};
    sessions.forEach(s => {
        if (s.id) {
            initialRsvps[s.id] = 'YES';
        }
    });

    const [rsvps, setRsvps] = useState<Record<string, RsvpOption>>(initialRsvps);
    const [playerNote, setPlayerNote] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    const handleRsvpChange = (sessionId: string, value: RsvpOption) => {
        setRsvps(prev => ({
            ...prev,
            [sessionId]: value
        }));
    };

    const hasAtLeastOneActive = Object.values(rsvps).some(r => r === 'YES' || r === 'MAYBE');

    const handleSubmit = async () => {
        if (!hasAtLeastOneActive) {
            message.warning(t('invitations.rsvp.requireOneActive', 'Please select YES or MAYBE for at least one session.'));
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                playerNote: playerNote.trim() || undefined,
                sessionRsvps: Object.entries(rsvps).map(([sessionId, rsvp]) => ({
                    sessionId,
                    rsvp
                }))
            };

            const response = await axios.post<JoinRequestDto>(
                `/api/launcher/invitations/${invitation.id}/join-requests`,
                payload
            );

            message.success(t('invitations.rsvp.submittedSuccess', 'Join request submitted successfully!'));
            onSuccess(response.data);
        } catch (err: any) {
            const errorMsg = err.response?.data || t('invitations.rsvp.submitError', 'Failed to submit join request. Please try again.');
            message.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const isWaitlist = !invitation.approvalRequired && invitation.availableSeats <= 0;

    return (
        <Card
            style={{
                borderRadius: 12,
                border: '1px solid #d9d9d9',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                background: '#fff',
                marginTop: 16
            }}
        >
            <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
                <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {t('invitations.rsvp.formTitle', 'Select Your Availability')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                {t('invitations.rsvp.formSubtitle', 'Indicate your RSVP status for each scheduled session and optionally add a note for the Game Master.')}
            </Paragraph>

            {isWaitlist && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('invitations.rsvp.waitlistNoticeTitle', 'Table is currently full')}
                    description={t('invitations.rsvp.waitlistNoticeDesc', 'This adventure has reached maximum player capacity. Your join request will be placed on the waitlist for the organizer to review if a spot opens up.')}
                    style={{ marginBottom: 16 }}
                />
            )}

            {!invitation.approvalRequired && !isWaitlist && (
                <Alert
                    type="success"
                    showIcon
                    message={t('invitations.rsvp.instantNoticeTitle', 'Instant Reservation')}
                    description={t('invitations.rsvp.instantNoticeDesc', 'This adventure has auto-approval enabled. Your seat will be confirmed immediately upon submission.')}
                    style={{ marginBottom: 16 }}
                />
            )}

            {invitation.approvalRequired && (
                <Alert
                    type="info"
                    showIcon
                    message={t('invitations.rsvp.approvalNoticeTitle', 'GM Approval Required')}
                    description={t('invitations.rsvp.approvalNoticeDesc', 'Your join request will be submitted to the Game Master / Organizer for review.')}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* SESSIONS LIST */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                {sessions.map((session, index) => {
                    const currentVal = session.id ? rsvps[session.id] || 'YES' : 'YES';
                    return (
                        <div
                            key={session.id || index}
                            style={{
                                padding: '12px 16px',
                                background: '#fafafa',
                                border: '1px solid #f0f0f0',
                                borderRadius: 8,
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 12
                            }}
                        >
                            <div>
                                <Text strong style={{ fontSize: 14 }}>
                                    {t('invitations.detail.sessionNum', 'Session')} #{index + 1}:
                                </Text>{' '}
                                <Text style={{ fontSize: 14 }}>
                                    {dayjs(session.time).format('dddd, MMMM D, YYYY [at] HH:mm')}
                                </Text>
                            </div>

                            <Radio.Group
                                value={currentVal}
                                onChange={(e) => session.id && handleRsvpChange(session.id, e.target.value)}
                                buttonStyle="solid"
                                size="small"
                            >
                                <Radio.Button value="YES" style={{ borderColor: currentVal === 'YES' ? '#52c41a' : undefined, background: currentVal === 'YES' ? '#52c41a' : undefined }}>
                                    <CheckCircleOutlined /> {t('invitations.rsvp.yes', 'YES')}
                                </Radio.Button>
                                <Radio.Button value="MAYBE" style={{ borderColor: currentVal === 'MAYBE' ? '#faad14' : undefined, background: currentVal === 'MAYBE' ? '#faad14' : undefined }}>
                                    <QuestionCircleOutlined /> {t('invitations.rsvp.maybe', 'MAYBE')}
                                </Radio.Button>
                                <Radio.Button value="NO" style={{ borderColor: currentVal === 'NO' ? '#ff4d4f' : undefined, background: currentVal === 'NO' ? '#ff4d4f' : undefined }}>
                                    <CloseCircleOutlined /> {t('invitations.rsvp.no', 'NO')}
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                    );
                })}
            </div>

            {/* OPTIONAL NOTE */}
            <div style={{ marginBottom: 20 }}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                    {t('invitations.rsvp.notesLabel', 'Optional Note for the Game Master')}
                </Text>
                <TextArea
                    value={playerNote}
                    onChange={(e) => setPlayerNote(e.target.value)}
                    maxLength={150}
                    showCount
                    rows={3}
                    placeholder={t('invitations.rsvp.notesPlaceholder', 'Introduce your character idea, experience with the game, or any questions for the GM (max 150 characters)...')}
                />
            </div>

            {/* ACTION BUTTONS */}
            <Flex vertical align="center" gap="middle" style={{ marginTop: 8 }}>
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitting}
                    disabled={!hasAtLeastOneActive}
                    onClick={handleSubmit}
                    style={{ width: '100%', maxWidth: '250px', background: '#52c41a', borderColor: '#52c41a' }}
                >
                    {t('invitations.rsvp.submitButton', 'Submit Reservation Request')}
                </Button>
                <Button onClick={onCancel} disabled={submitting} style={{ width: '100%', maxWidth: '250px' }}>
                    {t('common.cancel', 'Cancel')}
                </Button>
            </Flex>
        </Card>
    );
};
